import { isEqual } from "lodash";
import { useCallback, useRef } from "react";

import { AssessmentAnswer } from "../questions/types";

// The answer fields the save mutation takes. NPS answers save their score in
// integerAnswer: npsValue is read-only, and the tab never sends it.
const ANSWER_FIELDS = [
  "integerAnswer",
  "textAnswer",
  "choices",
  "comment",
] as const;
type AnswerField = (typeof ANSWER_FIELDS)[number];

// A text or comment is a JSON string. The tab holds JSON.stringify output, but
// the backend returns Python json.dumps output (with spaces), and MySQL can
// reorder the keys. So compare the parsed values. A string that is not JSON
// stays a string.
const parseJson = (value: unknown) => {
  if (typeof value !== "string") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

// question.tsx fills a missing text or comment with "{}", so an empty value
// can be null, undefined or "{}". They are the same for change tracking.
const normalize = (value: unknown) => {
  const parsed = parseJson(value);
  return parsed === undefined || isEqual(parsed, {}) ? null : parsed;
};
const isSameValue = (a: unknown, b: unknown) =>
  isEqual(normalize(a), normalize(b));

type FieldChange = { version: number; value: unknown };
type SentVersions = Map<number, Map<AnswerField, number>>;

/**
 * Tracks the answer fields this tab changed since its last successful save.
 *
 * A save sends only the changed answers, and in each one only the changed
 * fields. Every other field is sent as null, and the backend keeps its stored
 * value. So the tab cannot overwrite an answer or a comment that Claude, Ask AI
 * or another tab saved after this tab loaded the review (TF-1775). Example: the
 * user clicks a rating, and the save sends the rating but not the tab's old
 * empty comment.
 *
 * Each change gets a new version number. A save remembers the versions it
 * sent, and markSaved unmarks a field only when its version did not change
 * while the save ran.
 */
const useChangedAnswers = () => {
  const changes = useRef(new Map<number, Map<AnswerField, FieldChange>>());
  const nextVersion = useRef(0);
  const sentVersions = useRef(new WeakMap<AssessmentAnswer[], SentVersions>());

  // Compare the new answer with the tab's copy of it, field by field.
  const markChanged = useCallback(
    (previous: AssessmentAnswer | undefined, next: AssessmentAnswer) => {
      ANSWER_FIELDS.forEach((field) => {
        if (isSameValue(previous?.[field], next[field])) {
          return;
        }
        const fields = changes.current.get(next.questionId) ?? new Map();
        nextVersion.current += 1;
        fields.set(field, {
          version: nextVersion.current,
          value: next[field],
        });
        changes.current.set(next.questionId, fields);
      });
    },
    [],
  );

  const forgetChanges = useCallback((questionIds: number[]) => {
    questionIds.forEach((id) => changes.current.delete(id));
  }, []);

  // Pass the returned array to markSaved after the save succeeds.
  const getChangedAnswers = useCallback((answers: AssessmentAnswer[]) => {
    const sent: SentVersions = new Map();
    const changedAnswers = answers.flatMap((answer) => {
      const fields = changes.current.get(answer.questionId);
      if (!fields) {
        return [];
      }
      const input: AssessmentAnswer = {
        questionId: answer.questionId,
        integerAnswer: null,
        textAnswer: null,
        choices: null,
        comment: null,
      };
      const versions = new Map<AnswerField, number>();
      fields.forEach((change, field) => {
        // An autosave can send answers older than the last change. Their
        // value for that field is not the one the user typed last (it can be
        // the value the tab loaded), so the field stays null and the save does
        // not unmark the change. An answer with no field to send is left out.
        if (isSameValue(answer[field], change.value)) {
          Object.assign(input, { [field]: answer[field] ?? null });
          versions.set(field, change.version);
        }
      });
      if (versions.size === 0) {
        return [];
      }
      sent.set(answer.questionId, versions);
      return [input];
    });
    sentVersions.current.set(changedAnswers, sent);
    return changedAnswers;
  }, []);

  const hasChangedAnswers = useCallback(
    (answers: AssessmentAnswer[]) =>
      answers.some((answer) => changes.current.has(answer.questionId)),
    [],
  );

  // Call only after a save succeeds: a failed save keeps its fields marked as
  // changed. A field the tab changed again while the save ran stays marked.
  const markSaved = useCallback((savedAnswers: AssessmentAnswer[]) => {
    sentVersions.current.get(savedAnswers)?.forEach((versions, questionId) => {
      const fields = changes.current.get(questionId);
      if (!fields) {
        return;
      }
      versions.forEach((version, field) => {
        if (fields.get(field)?.version === version) {
          fields.delete(field);
        }
      });
      if (fields.size === 0) {
        changes.current.delete(questionId);
      }
    });
  }, []);

  // Takes the saved answer that Claude, ChatGPT or Ask AI wrote (TF-1786), but
  // keeps each field this tab changed and did not save yet. Returns null when
  // no field takes a new value, and the fields that do.
  const mergeSavedAnswer = useCallback(
    (current: AssessmentAnswer | undefined, saved: AssessmentAnswer) => {
      const changedFields = changes.current.get(saved.questionId);
      const updatedFields = ANSWER_FIELDS.filter(
        (field) =>
          !changedFields?.has(field) &&
          !isSameValue(current?.[field], saved[field]),
      );
      if (updatedFields.length === 0) {
        return null;
      }
      const answer: AssessmentAnswer = { ...current, ...saved };
      ANSWER_FIELDS.forEach((field) => {
        if (!updatedFields.includes(field)) {
          Object.assign(answer, { [field]: current?.[field] });
        }
      });
      return { answer, updatedFields };
    },
    [],
  );

  return {
    markChanged,
    mergeSavedAnswer,
    forgetChanges,
    getChangedAnswers,
    hasChangedAnswers,
    markSaved,
  };
};

export default useChangedAnswers;
